import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Home, Eye, EyeOff, Info } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { z } from "zod";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AdminLogger } from "@/lib/adminLogger";
import { getDashboardForRoles } from "@/lib/roleRedirect";
import { lovable } from "@/integrations/lovable/index";
import { SEO } from "@/components/SEO";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

const authSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"landlord" | "adjuster">("landlord");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [landlordInfoOpen, setLandlordInfoOpen] = useState(false);
  const [landlordInfo, setLandlordInfo] = useState({
    first_name: "",
    last_name: "",
    contact_email: "",
    contact_phone: "",
    contact_phone_ext: "",
    company: "",
    other_phone: "",
    other_email: "",
  });

  const finalizePendingApproval = useCallback(
    async (user: { id: string; email?: string | null; user_metadata?: { role?: string } }) => {
      try {
        await supabase.functions.invoke("notify-pending-approval", {
          body: {
            pending_user_id: user.id,
            pending_user_email: user.email,
            requested_role: user.user_metadata?.role ?? role,
          },
        });
      } catch (notifyErr) {
        console.error("Failed to notify admins of pending user:", notifyErr);
      }

      await supabase.auth.signOut();

      toast({
        title: "Account pending approval",
        description:
          "Your email is verified. An administrator will review and approve your account shortly. You'll be notified once approved.",
      });

      setVerificationEmailSent(false);
      setEmail("");
      setPassword("");
      window.history.replaceState({}, document.title, "/auth");
    },
    [role, toast],
  );

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast({
          title: "Google sign-in failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Google sign-in failed",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSignupVerification = params.get("verification") === "signup";

    // Check if user is already logged in. If the stored JWT is invalid
    // (e.g. "missing sub claim" / bad_jwt), clear it so login can proceed.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) return;

      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user) {
        await supabase.auth.signOut();
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
            .forEach((k) => localStorage.removeItem(k));
        } catch {}
        return;
      }

      if (isSignupVerification) {
        await finalizePendingApproval({
          id: userData.user.id,
          email: userData.user.email,
          user_metadata: userData.user.user_metadata,
        });
        return;
      }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      const roles = (roleData ?? []).map((r) => r.role as AppRole);
      navigate(getDashboardForRoles(roles));
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setTimeout(async () => {
          if (isSignupVerification) {
            await finalizePendingApproval({
              id: session.user.id,
              email: session.user.email,
              user_metadata: session.user.user_metadata,
            });
            return;
          }

          const { data: roleData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id);

          const roles = (roleData ?? []).map((r) => r.role as AppRole);
          navigate(getDashboardForRoles(roles));
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [finalizePendingApproval, navigate]);

  

  const performSignUp = async (extraMetadata: Record<string, string> = {}) => {
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Client-side guard: pre-check for an existing profile with this email
      // to prevent duplicate pending rows on signup retries.
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id, is_disabled, disabled_reason")
        .ilike("email", normalizedEmail)
        .maybeSingle();

      if (existingProfile) {
        const pending =
          existingProfile.is_disabled &&
          existingProfile.disabled_reason === "Pending admin approval";
        toast({
          title: pending ? "Already awaiting approval" : "Account exists",
          description: pending
            ? "An account with this email is already pending admin approval."
            : "This email is already registered. Please log in instead.",
          variant: "destructive",
        });
        return false;
      }

      const redirectUrl = `${window.location.origin}/auth?verification=signup`;

      const { data: signUpData, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            role,
            ...extraMetadata,
          },
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: "Account exists",
            description: "This email is already registered. Please log in instead.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Sign up failed",
            description: error.message,
            variant: "destructive",
          });
        }
        return false;
      }

      // Supabase returns a user with an empty `identities` array when the email
      // is already registered but unconfirmed — and it does NOT send a new code
      // in that case. Detect this and explicitly resend the verification code.
      const isRepeatedSignup =
        !!signUpData?.user &&
        Array.isArray(signUpData.user.identities) &&
        signUpData.user.identities.length === 0;

      if (isRepeatedSignup) {
        const { error: resendError } = await supabase.auth.resend({
          type: "signup",
          email: normalizedEmail,
        });
        if (resendError) {
          toast({
            title: "Couldn't send code",
            description: resendError.message,
            variant: "destructive",
          });
          return false;
        }
      }

      setVerificationEmailSent(true);
      toast({
        title: "Check your email",
        description: "We've sent you a verification email. Open the link in that email to continue.",
      });
      return true;
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      authSchema.parse({ email, password });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
      return;
    }

    if (role === "landlord") {
      setLandlordInfo((prev) => ({
        ...prev,
        contact_email: prev.contact_email || email.trim().toLowerCase(),
      }));
      setLandlordInfoOpen(true);
      return;
    }

    await performSignUp();
  };

  const handleLandlordInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { first_name, last_name, contact_email, contact_phone } = landlordInfo;
    if (!first_name.trim() || !last_name.trim() || !contact_email.trim() || !contact_phone.trim()) {
      toast({
        title: "Missing required fields",
        description: "First name, last name, email, and phone number are required.",
        variant: "destructive",
      });
      return;
    }
    try {
      z.string().email().parse(contact_email);
    } catch {
      toast({
        title: "Invalid email",
        description: "Please enter a valid contact email address.",
        variant: "destructive",
      });
      return;
    }
    const ok = await performSignUp({
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      contact_email: contact_email.trim(),
      contact_phone: contact_phone.trim(),
      contact_phone_ext: landlordInfo.contact_phone_ext.trim(),
      company: landlordInfo.company.trim(),
      other_phone: landlordInfo.other_phone.trim(),
      other_email: landlordInfo.other_email.trim(),
    });
    if (ok) setLandlordInfoOpen(false);
  };


  const handleResendOtp = async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
      });
      if (error) {
        toast({ title: "Resend failed", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Email resent", description: "Check your inbox for a new verification email." });
      }
    } finally {
      setResending(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      authSchema.parse({ email, password });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Login failed",
            description: "Invalid email or password. Please try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Login failed",
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (data.user) {
        // Check if account is disabled (e.g., blacklisted domain).
        // Fail-closed: if the lookup errors, sign the user out rather than letting them in.
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("is_disabled, disabled_reason")
          .eq("id", data.user.id)
          .maybeSingle();

        if (profileError) {
          await supabase.auth.signOut();
          toast({
            title: "Sign-in error",
            description: "Could not verify account status. Please try again.",
            variant: "destructive",
          });
          return;
        }

        if (profile?.is_disabled === true) {
          await supabase.auth.signOut();
          toast({
            title: "Account disabled",
            description: profile.disabled_reason || "Your account has been disabled. Contact an administrator.",
            variant: "destructive",
          });
          return;
        }

        // Log the successful login (fire-and-forget so it never blocks sign-in)
        void AdminLogger.login();

        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", data.user.id);

        const roles = (roleData ?? []).map((r) => r.role as AppRole);
        navigate(getDashboardForRoles(roles));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate email
      z.string().email().parse(forgotPasswordEmail);

      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(
        forgotPasswordEmail,
        {
          redirectTo: redirectUrl,
        }
      );

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Check your email",
          description: "We've sent you a password reset link.",
        });
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: "Please enter a valid email address.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign In or Sign Up"
        description="Sign in to your TPS Housing account or create a new one to manage claims, placements, and properties."
        canonicalUrl="/auth"
        noIndex
      />
    <div className="min-h-screen flex items-center justify-center bg-secondary p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Home className="w-8 h-8 text-primary" />
          <span className="text-2xl font-bold text-foreground">TPS Housing</span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>Sign in to your account or create a new one</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <div className="mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading || loading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {googleLoading ? "Signing in..." : "Continue with Google"}
                  </Button>
                </div>
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                  </div>
                </div>
                {!showForgotPassword ? (
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">Password</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="text-sm p-0 h-auto"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot Password?
                        </Button>
                      </div>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email Address</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        required
                      />
                      <p className="text-sm text-muted-foreground">
                        Enter your email and we'll send you a reset link
                      </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Sending..." : "Send Reset Link"}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="w-full"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail("");
                      }}
                    >
                      Back to Login
                    </Button>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="signup">
                {verificationEmailSent ? (
                  <div className="space-y-4">
                    <div className="space-y-2 text-center">
                      <h3 className="font-semibold">Verify your email</h3>
                      <p className="text-sm text-muted-foreground">
                        We sent a verification link to <span className="font-medium text-foreground">{email}</span>
                      </p>
                        <p className="text-sm text-muted-foreground">
                          Click the link in that email to verify your address and finish account setup.
                        </p>
                      </div>
                    <div className="flex justify-between text-sm">
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 h-auto"
                        onClick={handleResendOtp}
                        disabled={resending}
                      >
                        {resending ? "Resending..." : "Resend code"}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => {
                          setVerificationEmailSent(false);
                        }}
                      >
                        Use different email
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleGoogleSignIn}
                        disabled={googleLoading || loading}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        {googleLoading ? "Signing in..." : "Continue with Google"}
                      </Button>
                    </div>
                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                      </div>
                    </div>
                    <form onSubmit={handleSignUp} className="space-y-4">
                      <div className="space-y-3">
                        <Label>Account Type</Label>
                        <RadioGroup value={role} onValueChange={(value) => setRole(value as "landlord" | "adjuster")}>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="landlord" id="landlord" />
                            <Label htmlFor="landlord" className="font-normal cursor-pointer">
                              Landlord - I want to list properties
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="adjuster" id="adjuster" />
                            <Label htmlFor="adjuster" className="font-normal cursor-pointer">
                              Adjuster - I want to submit a new placement request
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email">Email</Label>
                        <Input
                          id="signup-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-semibold mb-1">Password Requirements:</p>
                                <ul className="text-sm space-y-1 list-disc list-inside">
                                  <li>At least 6 characters</li>
                                  <li>Include uppercase and lowercase letters</li>
                                  <li>Include at least one number</li>
                                  <li>Include at least one special character</li>
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? "Sending email..." : "Create Account"}
                      </Button>
                    </form>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => navigate("/")} className="text-sm">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={landlordInfoOpen} onOpenChange={(open) => !loading && setLandlordInfoOpen(open)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Your personal information</DialogTitle>
            <DialogDescription>
              Tell us a bit about yourself. Fields marked with <span className="text-destructive">*</span> are required.
              You can edit these later in your profile.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLandlordInfoSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="li-first">First name <span className="text-destructive">*</span></Label>
                <Input
                  id="li-first"
                  value={landlordInfo.first_name}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="li-last">Last name <span className="text-destructive">*</span></Label>
                <Input
                  id="li-last"
                  value={landlordInfo.last_name}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="li-email">Email <span className="text-destructive">*</span></Label>
              <Input
                id="li-email"
                type="email"
                value={landlordInfo.contact_email}
                onChange={(e) => setLandlordInfo({ ...landlordInfo, contact_email: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="li-phone">Phone number <span className="text-destructive">*</span></Label>
                <Input
                  id="li-phone"
                  type="tel"
                  value={landlordInfo.contact_phone}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, contact_phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="li-ext">Ext</Label>
                <Input
                  id="li-ext"
                  value={landlordInfo.contact_phone_ext}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, contact_phone_ext: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="li-company">Company</Label>
              <Input
                id="li-company"
                value={landlordInfo.company}
                onChange={(e) => setLandlordInfo({ ...landlordInfo, company: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="li-other-phone">Other phone</Label>
                <Input
                  id="li-other-phone"
                  type="tel"
                  value={landlordInfo.other_phone}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, other_phone: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="li-other-email">Other email</Label>
                <Input
                  id="li-other-email"
                  type="email"
                  value={landlordInfo.other_email}
                  onChange={(e) => setLandlordInfo({ ...landlordInfo, other_email: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={() => setLandlordInfoOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default Auth;
